import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '../Button'

export interface FormField {
  name: string
  label: string
  type?: 'text' | 'number' | 'textarea' | 'checkbox'
  placeholder?: string
  required?: boolean
  value: any
  onChange: (value: any) => void
}

interface EditModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  fields: FormField[]
  onSubmit: () => void
  onCancel?: () => void
  isLoading?: boolean
  submitText?: string
  cancelText?: string
}

export const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  onClose,
  title,
  fields,
  onSubmit,
  onCancel,
  isLoading = false,
  submitText = 'Сохранить',
  cancelText = 'Отмена'
}) => {
  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit()
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    onClose()
  }

  const renderField = (field: FormField) => {
    const commonProps = {
      id: field.name,
      name: field.name,
      placeholder: field.placeholder,
      required: field.required,
      disabled: isLoading
    }

    switch (field.type) {
      case 'textarea':
        return (
          <div key={field.name}>
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              {...commonProps}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
            />
          </div>
        )
      
      case 'number':
        return (
          <div key={field.name}>
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              {...commonProps}
              type="number"
              value={field.value}
              onChange={(e) => field.onChange(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
            />
          </div>
        )
      
      case 'checkbox':
        return (
          <div key={field.name} className="flex items-center">
            <input
              {...commonProps}
              type="checkbox"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
            />
            <label htmlFor={field.name} className="ml-2 text-sm text-gray-700">
              {field.label}
            </label>
          </div>
        )
      
      default:
        return (
          <div key={field.name}>
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              {...commonProps}
              type="text"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
            />
          </div>
        )
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4 mb-6">
            {fields.map(renderField)}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={isLoading}
            >
              {cancelText}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Сохранение...' : submitText}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
















